import { addDaysIsoDate, todayIsoDate } from '@/lib/formatters';
import type {
  SoftwareBillingCycle,
  SoftwareCategory,
  SoftwareStatus,
  Tables,
} from '@/types/database';

type Subscription = Tables<'software_subscriptions'>;
type Seat = Tables<'software_seats'>;

export const SOFTWARE_CATEGORIES: SoftwareCategory[] = [
  'design',
  'creative',
  'productivity',
  'communication',
  'marketing',
  'ecommerce',
  'finance',
  'development',
  'operations',
  'other',
];

export const SOFTWARE_CATEGORY_LABELS: Record<SoftwareCategory, string> = {
  design: 'Design & engineering',
  creative: 'Creative',
  productivity: 'Productivity',
  communication: 'Communication',
  marketing: 'Marketing & social',
  ecommerce: 'Sales & e-commerce',
  finance: 'Finance & HR',
  development: 'IT & development',
  operations: 'Operations & shipping',
  other: 'Other',
};

export const SOFTWARE_STATUSES: SoftwareStatus[] = ['active', 'trial', 'paused', 'cancelled'];

export const SOFTWARE_STATUS_LABELS: Record<SoftwareStatus, string> = {
  active: 'Active',
  trial: 'Trial',
  paused: 'Paused',
  cancelled: 'Cancelled',
};

export const BILLING_CYCLES: SoftwareBillingCycle[] = [
  'monthly',
  'quarterly',
  'annual',
  'one_time',
];

export const BILLING_CYCLE_LABELS: Record<SoftwareBillingCycle, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
  one_time: 'One-time',
};

/** Suffix for a price billed on this cycle, e.g. "$20 / mo". */
export const BILLING_CYCLE_UNIT: Record<SoftwareBillingCycle, string> = {
  monthly: 'mo',
  quarterly: 'quarter',
  annual: 'yr',
  one_time: 'one-time',
};

const CYCLE_MONTHS: Record<SoftwareBillingCycle, number | null> = {
  monthly: 1,
  quarterly: 3,
  annual: 12,
  one_time: null,
};

/** Statuses that are still being billed, so they count toward spend. */
export function isBilled(status: SoftwareStatus): boolean {
  return status === 'active';
}

export interface SoftwareCosts {
  /** Seats the price is multiplied by. Null for flat-priced plans. */
  billedSeats: number | null;
  /** What each bill comes to, every seat included. */
  perBillCents: number | null;
  monthlyCents: number | null;
  yearlyCents: number | null;
  /** Yearly price of the annual plan for the same seats. */
  annualPlanCents: number | null;
  /** Current yearly cost minus the annual plan. Negative = annual costs more. */
  annualSavingsCents: number | null;
}

/**
 * A per-seat plan with no explicit seat count is billed for the seats
 * that are actually assigned, which is how most SaaS admin consoles bill.
 */
export function softwareCosts(
  sub: Pick<
    Subscription,
    | 'billing_cycle'
    | 'price_cents'
    | 'price_per_seat'
    | 'seat_count'
    | 'annual_option_available'
    | 'annual_price_cents'
  >,
  assignedSeats: number,
): SoftwareCosts {
  const billedSeats = sub.price_per_seat ? (sub.seat_count ?? assignedSeats) : null;
  const total = (cents: number | null) =>
    cents == null ? null : billedSeats == null ? cents : cents * billedSeats;

  const perBillCents = total(sub.price_cents);
  const months = CYCLE_MONTHS[sub.billing_cycle];
  const monthlyCents =
    perBillCents == null || months == null ? null : Math.round(perBillCents / months);
  const yearlyCents =
    perBillCents == null || months == null ? null : Math.round((perBillCents * 12) / months);
  const annualPlanCents = sub.annual_option_available ? total(sub.annual_price_cents) : null;
  const canSwitch = sub.billing_cycle === 'monthly' || sub.billing_cycle === 'quarterly';
  const annualSavingsCents =
    canSwitch && annualPlanCents != null && yearlyCents != null
      ? yearlyCents - annualPlanCents
      : null;

  return {
    billedSeats,
    perBillCents,
    monthlyCents,
    yearlyCents,
    annualPlanCents,
    annualSavingsCents,
  };
}

export type AnnualOptionState =
  | 'already_annual'
  | 'one_time'
  | 'unknown'
  | 'unavailable'
  | 'available'
  | 'cheaper'
  | 'not_cheaper';

export const ANNUAL_OPTION_LABELS: Record<AnnualOptionState, string> = {
  already_annual: 'Already annual',
  one_time: 'One-time purchase',
  unknown: 'Not checked',
  unavailable: 'No annual plan',
  available: 'Annual plan offered',
  cheaper: 'Annual is cheaper',
  not_cheaper: 'Annual isn’t cheaper',
};

export function annualOptionState(
  sub: Pick<Subscription, 'billing_cycle' | 'annual_option_available'>,
  costs: Pick<SoftwareCosts, 'annualSavingsCents'>,
): AnnualOptionState {
  if (sub.billing_cycle === 'annual') return 'already_annual';
  if (sub.billing_cycle === 'one_time') return 'one_time';
  if (sub.annual_option_available == null) return 'unknown';
  if (!sub.annual_option_available) return 'unavailable';
  if (costs.annualSavingsCents == null) return 'available';
  return costs.annualSavingsCents > 0 ? 'cheaper' : 'not_cheaper';
}

export type RenewalState = 'none' | 'scheduled' | 'soon' | 'overdue';

/**
 * "Soon" is the next 30 days — enough notice to cancel or renegotiate
 * before an auto-renewal charges the card.
 */
export const RENEWAL_SOON_DAYS = 30;

export function renewalState(sub: Pick<Subscription, 'renews_on' | 'status'>): RenewalState {
  if (!sub.renews_on || sub.status === 'cancelled') return 'none';
  const day = sub.renews_on.slice(0, 10);
  const today = todayIsoDate();
  if (day < today) return 'overdue';
  if (day <= addDaysIsoDate(today, RENEWAL_SOON_DAYS)) return 'soon';
  return 'scheduled';
}

/** "2 years, 3 months" since a `YYYY-MM-DD` start date. */
export function durationSince(isoDate: string | null | undefined): string | null {
  if (!isoDate) return null;
  const [y, m, d] = isoDate.slice(0, 10).split('-').map(Number);
  const now = new Date();
  let months = (now.getFullYear() - y) * 12 + (now.getMonth() + 1 - m);
  if (now.getDate() < d) months -= 1;
  if (months < 0) return 'Not started yet';
  if (months === 0) return 'Less than a month';
  const years = Math.floor(months / 12);
  const rest = months % 12;
  const parts: string[] = [];
  if (years) parts.push(`${years} ${years === 1 ? 'year' : 'years'}`);
  if (rest) parts.push(`${rest} ${rest === 1 ? 'month' : 'months'}`);
  return parts.join(', ');
}

export interface SeatProfile {
  id: string;
  full_name: string | null;
  email: string;
  avatar_url?: string | null;
}

export function seatHolderName(
  seat: Pick<Seat, 'profile_id' | 'assignee_name'>,
  profilesById: Map<string, SeatProfile>,
): string {
  const profile = seat.profile_id ? profilesById.get(seat.profile_id) : undefined;
  return profile?.full_name || profile?.email || seat.assignee_name || 'Unassigned';
}

/** `list` plus the profile already on the record, so a select never shows blank. */
export function withCurrentProfile(
  list: SeatProfile[],
  currentId: string | null | undefined,
  profilesById: Map<string, SeatProfile>,
): SeatProfile[] {
  if (!currentId || list.some((p) => p.id === currentId)) return list;
  const current = profilesById.get(currentId);
  return current ? [...list, current] : list;
}

export interface SoftwareRow extends Subscription {
  seats: Seat[];
  seatNames: string[];
  ownerName: string | null;
  costs: SoftwareCosts;
  annualState: AnnualOptionState;
}

export function buildSoftwareRows(
  subscriptions: Subscription[],
  seats: Seat[],
  profilesById: Map<string, SeatProfile>,
): SoftwareRow[] {
  const seatsBySub = new Map<string, Seat[]>();
  for (const seat of seats) {
    const list = seatsBySub.get(seat.subscription_id);
    if (list) list.push(seat);
    else seatsBySub.set(seat.subscription_id, [seat]);
  }
  return subscriptions.map((sub) => {
    const subSeats = seatsBySub.get(sub.id) ?? [];
    const seatNames = subSeats
      .map((seat) => seatHolderName(seat, profilesById))
      .sort((a, b) => a.localeCompare(b));
    const costs = softwareCosts(sub, subSeats.length);
    const owner = sub.owner_id ? profilesById.get(sub.owner_id) : undefined;
    return {
      ...sub,
      seats: subSeats,
      seatNames,
      ownerName: owner ? owner.full_name || owner.email : null,
      costs,
      annualState: annualOptionState(sub, costs),
    };
  });
}

export function websiteHref(website: string | null | undefined): string | null {
  const trimmed = website?.trim();
  if (!trimmed) return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

export function websiteLabel(website: string): string {
  return website.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

export function friendlySoftwareError(err: unknown, fallback: string): string {
  const msg =
    err instanceof Error
      ? err.message
      : typeof err === 'object' && err && 'message' in err
        ? String((err as { message: unknown }).message)
        : '';
  if (/software_subscriptions_name_uniq/i.test(msg)) {
    return 'Software with that name is already on the list.';
  }
  if (/software_seats_profile_uniq/i.test(msg)) {
    return 'That person already has a seat on this subscription.';
  }
  if (/software_seats_assignee/i.test(msg)) return 'Pick a person or enter a name for the seat.';
  if (/annual_price_needs_option/i.test(msg)) {
    return 'Mark the annual plan as available before entering its price.';
  }
  if (/row-level security|permission denied/i.test(msg)) {
    return 'Only admins can change software records.';
  }
  return msg || fallback;
}
